import { computed, reactive, ref, watch } from 'vue'
import type { Ref } from 'vue'
import { useI18n } from 'vue-i18n'

// models
import LocationProduct from '../../../../../models/LocationProduct'
import Location from '../../../../../models/Location'

// misc
import axios from 'axios'
import { Temporal } from '@js-temporal/polyfill'
import NotificationService from '../../../../../services/NotificationService'
import VerticalRegistry from '../../../../../verticals/VerticalRegistry'
import InventoryApi from '../../../../../services/api-clients/InventoryApi'
import type { DateRange } from '../../../../../types/misc'
import ParcsApi from '../../../../../services/api-clients/ParcsApi'
import type { DayInventory, MonthInventory } from '../types'

export function useLocationData(
  location: Ref<null | Location>,
  calendarType: Ref<null | string>,
  dayDate: Ref<null | Temporal.PlainDate>,
  monthRange: Ref<null | DateRange>,
) {

  const { t } = useI18n()
  const verticalsToShow = VerticalRegistry.getMany(['showOnUniversalInventoryCalendar']).map((vertical) => vertical.name)
  const loadingLocationProducts = ref<boolean>(false)
  let abortController: null | AbortController = null

  const pendingDayCalls = ref<number>(0)
  const loadingDayInventory = computed<boolean>(() => pendingDayCalls.value > 0)

  const pendingMonthCalls = ref<number>(0)
  const loadingMonthInventory = computed<boolean>(() => pendingMonthCalls.value > 0)

  /**
   * All location data
   */

  watch(location, () => {

    if (!location.value) {
      return
    }

    const locationId = location.value.ID

    Promise.all([
      loadLocationProducts(locationId),
      // loadInventory(locationId),
    ])

  }, {immediate: true})

  /**
   * Location Products
   */

  function loadLocationProducts(locationId: number): Promise<any> {

    loadingLocationProducts.value = true

    return LocationProduct.fetchByLocationID(locationId)
      .catch(e => {
        if (axios.isCancel(e)) {
          return
        }
        console.error(e)
        NotificationService.error(t('alerts.couldnt_load_products'))
        throw e
      })
      .finally(() => {
        loadingLocationProducts.value = false
      })

  }

  const locationProducts = computed<LocationProduct[]>(() => {

    if (location.value === null) {
      return []
    }

    return LocationProduct.query()
      .with(['product'])
      .where('locationID', location.value.ID)
      .whereHas('product', (query) => {
        query.where('verticalType', verticalsToShow)
      })
      .get()
      .sort((lp1, lp2) => {

        const name1 = lp1.product?.name
        const name2 = lp2.product?.name

        if (name1 !== undefined && name2 === undefined) {
          return -1 // lp1 comes first
        }

        if (name2 === undefined && name2 !== undefined) {
          return 1 // lp2 comes first
        }

        if (name1 === undefined && name2 === undefined) {
          return 0 // literally don't care
        }

        return name1.localeCompare(name2)

      })
      .sort((lp1, lp2) => {
        const weight1 = VerticalRegistry.get(lp1.product?.verticalType)?.sortWeight ?? 1000
        const weight2 = VerticalRegistry.get(lp2.product?.verticalType)?.sortWeight ?? 1000
        return weight1 - weight2
      })

  })

  /**
   * Inventory
   */

  const dayInventory = reactive<DayInventory>({
    nonParcs: {windows: [], blackouts: {}, events: {}},
    parcs: {windows: []},
  })

  const monthInventory = reactive<MonthInventory>({
    nonParcs: {inventory: [], blackouts: []},
    parcs: {dates: {}},
  })

  watch([location, calendarType, dayDate], () => {
    if (location.value && calendarType.value === 'day') {
      loadDayInventory(location.value.ID)
    }
  }, {immediate: true})

  watch([location, calendarType, monthRange], () => {
    if (monthRange.value && location.value && calendarType.value === 'month') {
      loadMonthInventory(location.value.ID)
    }
  }, {immediate: true})

  async function loadDayInventory(locationId: number) {

    if (abortController) {
      abortController.abort()
      abortController = null
    }

    if (!location.value || !dayDate.value) {
      return
    }

    dayInventory.nonParcs.windows = []
    dayInventory.nonParcs.blackouts = {}
    dayInventory.nonParcs.events = {}
    dayInventory.parcs.windows = []
    pendingDayCalls.value++
    abortController = new AbortController()

    let loadedNonParcs = null, loadedParcs = null
    const promises: Promise<any>[] = []
    const isoDate = dayDate.value?.toString()

    promises.push(
      InventoryApi
        .getWindows(locationId, isoDate, undefined, abortController.signal)
        .then(result => {
          loadedNonParcs = result
        })
    )

    promises.push(
      ParcsApi
        .getOccupancyWindows(locationId, isoDate, abortController.signal)
        .then(result => {
          loadedParcs = result
        })
    )

    try {
      await Promise.all(promises).finally(() => {
        pendingDayCalls.value--
      })
    } catch (e) {
      if (!axios.isCancel(e)) {
        console.error(e)
        NotificationService.error(t('alerts.couldnt_load_inventory'))
      }
      return
    }

    abortController = null
    dayInventory.nonParcs = loadedNonParcs!
    dayInventory.parcs = loadedParcs!

  }

  async function loadMonthInventory(locationId: number) {

    if (abortController) {
      abortController.abort()
      abortController = null
    }

    if (!location.value || !monthRange.value?.start || !monthRange.value?.end) {
      return
    }

    monthInventory.nonParcs.inventory = []
    monthInventory.nonParcs.blackouts = []
    monthInventory.parcs.dates = {}
    pendingMonthCalls.value++
    abortController = new AbortController()

    let loadedNonParcs = null, loadedParcs = null
    const promises: Promise<any>[] = []
    const isoStart = monthRange.value.start.toString()
    const isoEnd = monthRange.value.end.toString()

    promises.push(
      InventoryApi
        .getInventories(locationId, isoStart, isoEnd, abortController.signal)
        .then(result => {
          loadedNonParcs = result
        })
    )

    promises.push(
      ParcsApi
        .getOccupancy(locationId, isoStart, isoEnd, abortController.signal)
        .then(result => {
          loadedParcs = result
        })
    )

    try {
      await Promise.all(promises).finally(() => {
        pendingMonthCalls.value--
      })
    } catch (e) {
      if (!axios.isCancel(e)) {
        NotificationService.error(t('alerts.couldnt_load_inventory'))
      }
      return
    }

    abortController = null
    monthInventory.nonParcs = loadedNonParcs!
    monthInventory.parcs = loadedParcs!

  }

  return {
    locationProducts,
    loadingLocationProducts,
    dayInventory,
    loadDayInventory,
    loadingDayInventory,
    monthInventory,
    loadMonthInventory,
    loadingMonthInventory,
  }

}
